import "@/App.css";
import { lazy, Suspense } from "react";
import { ACCOUNTING_PAGES, FINANCE_PAGES } from "./app/pageCatalog";
import { SISTER_PAGES } from "./features/sisterCompanies/access";

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { MasterAuthProvider } from "@/context/MasterAuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { ROUTER_BASENAME } from "@/lib/tenant";
import SessionUnavailable from "./components/SessionUnavailable";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { canViewPage, roleHome } from "./features/roles/pageAccess";
import PageAccessBoundary from "./features/roles/components/PageAccessBoundary";
import FullPageLoader from "./components/BrandedLoader";
import PageLoader from "./components/PageLoader";

import Layout from "@/components/Layout";
import DomainRootRoute from "./app/DomainRootRoute";

import MasterLayout from "@/components/MasterLayout";

const TenantHome = lazy(() => import("@/pages/public/TenantHome"));
const AvailablePlots = lazy(() => import("@/pages/public/AvailablePlots"));
const PublicCustomerLedger = lazy(() => import("./features/finance/pages/PublicCustomerLedger"));
const Login = lazy(() => import("./features/authentication/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Inbox = lazy(() => import("@/pages/Inbox"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const Leads = lazy(() => import("@/pages/Leads"));
const Cases = lazy(() => import("@/pages/Cases"));
const Rules = lazy(() => import("@/pages/Rules"));
const AutomationStudio = lazy(() => import("@/pages/AutomationStudio"));
const Appointments = lazy(() => import("@/pages/Appointments"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Knowledge = lazy(() => import("@/pages/Knowledge"));
const Settings = lazy(() => import("@/pages/Settings"));
const WhatsAppMessages = lazy(() => import("@/pages/WhatsAppMessages"));
const FailedMessages = lazy(() => import("@/pages/FailedMessages"));
const Monitoring = lazy(() => import("@/pages/Monitoring"));
const Webhooks = lazy(() => import("@/pages/Webhooks"));
const ApiLogs = lazy(() => import("@/pages/ApiLogs"));
const AuditLogs = lazy(() => import("@/pages/AuditLogs"));
const AllJobs = lazy(() => import("./features/jobs/AllJobs"));
const RequestTrace = lazy(() => import("@/pages/RequestTrace"));
const Reports = lazy(() => import("@/pages/Reports"));
const Roles = lazy(() => import("@/pages/Roles"));
const PlotInventory = lazy(() => import("@/pages/PlotInventory"));
const InventoryDashboard = lazy(() => import("@/pages/InventoryDashboard"));
const Projects = lazy(() => import("./features/projects/pages/Projects"));
const Blocks = lazy(() => import("./features/blocks/pages/Blocks"));
const Bookings = lazy(() => import("@/pages/Bookings"));
const BookingDashboard = lazy(() => import("@/pages/BookingDashboard"));
const CreateBooking = lazy(() => import("@/pages/CreateBooking"));
const ConfirmedBookings = lazy(() => import("@/features/bookings/pages/ConfirmedBookings"));
const ExistingBooking = lazy(() => import("@/pages/ExistingBooking"));
const Dealers = lazy(() => import("@/pages/Dealers"));
const DealerPortal = lazy(() => import("@/pages/dealer/DealerPortal"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const CustomerPortal = lazy(() => import("@/pages/customer/CustomerPortal"));
const CustomerRegister = lazy(() => import("@/pages/customer/CustomerPortal").then(module => ({ default: module.CustomerRegister })));
const Receipts = lazy(() => import("@/pages/Receipts"));
const Accounting = lazy(() => import("@/pages/Accounting"));
const Finance = lazy(() => import("@/pages/Finance"));
const SisterCompanies = lazy(() => import("./features/sisterCompanies/pages/SisterCompanies"));
const ApiIntegrations = lazy(() => import("@/pages/ApiIntegrations"));
const ReceiptPrint = lazy(() => import("@/pages/ReceiptPrint"));
const ChallanPrint = lazy(() => import("@/features/bookings/pages/ChallanPrint"));
const MasterLogin = lazy(() => import("@/pages/master/MasterLogin"));
const MasterDashboard = lazy(() => import("@/pages/master/MasterDashboard"));
const Merchants = lazy(() => import("@/pages/master/Merchants"));
const MerchantWizard = lazy(() => import("@/pages/master/MerchantWizard"));
const MerchantDetail = lazy(() => import("@/pages/master/MerchantDetail"));
const MasterPlans = lazy(() => import("@/pages/master/MasterPlans"));
const MasterUsers = lazy(() => import("@/pages/master/MasterUsers"));
const MasterIntegrations = lazy(() => import("@/pages/master/MasterIntegrations"));
const MasterApiIntegrations = lazy(() => import("@/pages/master/MasterApiIntegrations"));
const MasterWhatsApp = lazy(() => import("@/pages/master/MasterWhatsApp"));
const MasterCompanions = lazy(() => import("./features/companions/MasterCompanions"));
const MerchantCompanions = lazy(() => import("./features/companions/MerchantCompanions"));
const MasterOperations = lazy(() => import("@/pages/master/MasterOperations"));
const MasterSecurity = lazy(() => import("@/pages/master/MasterSecurity"));
const PlatformSettings = lazy(() => import("@/pages/master/PlatformSettings"));

const STAFF = ["super_admin", "admin", "supervisor", "agent", "auditor"];
const ADMINISH = ["super_admin", "admin"];
const LOGS = ["super_admin", "admin", "supervisor", "auditor"];
const OPS = ["super_admin", "admin", "supervisor", "agent"];
const AIMGMT = ["super_admin", "admin", "supervisor"];
const REPORTS = ["super_admin", "admin", "supervisor", "auditor"];

// Direct platform URLs have a merchant prefix; custom domains use the same
// routes without one. TenantProvider resolves the merchant independently.
const tenantCode = String(ROUTER_BASENAME || "")
  .trim()
  .replace(/^\/+|\/+$/g, "");

const TENANT_BASE = tenantCode ? `/${tenantCode}` : "";

function tenantUrl(path = "") {
  const cleanPath = String(path).replace(/^\/+|\/+$/g, "");
  return cleanPath ? `${TENANT_BASE}/${cleanPath}` : TENANT_BASE;
}

// These providers mount only when a tenant route matches.
// Preserve the provider order from your existing application.
function TenantRouteLayout() {
  return (
    <AuthProvider>
      <TenantProvider>
        <RouteErrorBoundary><Outlet /></RouteErrorBoundary>
      </TenantProvider>
    </AuthProvider>
  );
}

// Master pages use their own providers.
function MasterRouteLayout() {
  return (
    <BrandingProvider>
      <MasterAuthProvider>
        <RouteErrorBoundary><Outlet /></RouteErrorBoundary>
      </MasterAuthProvider>
    </BrandingProvider>
  );
}

function Protected({ children, roles }) {
  const { user, loading, error, retry, logout, refreshAccess } = useAuth();
  const location = useLocation();

  if (loading) {
    return <FullPageLoader label="Loading your workspace…" />;
  }

  if (error) return <SessionUnavailable message={error} onRetry={retry} />;
  if (!user) {
    return <Navigate to={tenantUrl("login")} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    const home =
      user.role === "sister_company"
        ? tenantUrl("sister-companies")
        : user.role === "sales_dealer"
        ? tenantUrl("dealer")
        : user.role === "customer"
          ? tenantUrl("portal")
          : STAFF.includes(user.role)
            ? tenantUrl("admin")
            : null;

    // Avoid redirect loops for unknown roles.
    if (!home) {
      return <div role="alert">You do not have access to this page.</div>;
    }

    return <Navigate to={home} replace />;
  }

  const path = TENANT_BASE && location.pathname.startsWith(TENANT_BASE + "/") ? location.pathname.slice(TENANT_BASE.length) : location.pathname;
  const home = roleHome(user);
  const homeUrl = home && canViewPage(user, home) && path !== home ? tenantUrl(home) : null;
  return <PageAccessBoundary user={user} path={path} homeUrl={homeUrl} onRefresh={refreshAccess} onLogout={logout}>{children}</PageAccessBoundary>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RouteErrorBoundary>
        <Suspense fallback={<PageLoader label="Loading page…" />}>
        <Routes>
          {/* Public root chooses a mapped back office without mounting authentication. */}
          <Route path="/" element={<DomainRootRoute />} />
          {/* Scoped statement links do not mount staff authentication providers. */}
          {TENANT_BASE && <>
            <Route path={`${TENANT_BASE}/ledger/:consumerNumber`} element={<PublicCustomerLedger />} />
            <Route path={`${TENANT_BASE}/ledger`} element={<PublicCustomerLedger />} />
            <Route path={`${TENANT_BASE}/customer-ledger`} element={<PublicCustomerLedger />} />
          </>}

          {/* Master administration */}
          <Route path="/master" element={<MasterRouteLayout />}>
            <Route path="login" element={<MasterLogin />} />

            <Route element={<MasterLayout />}>
              <Route index element={<MasterDashboard />} />
              <Route path="merchants" element={<Merchants />} />
              <Route path="merchants/new" element={<MerchantWizard />} />
              <Route path="merchants/:id" element={<MerchantDetail />} />
              <Route path="plans" element={<MasterPlans />} />
              <Route path="users" element={<MasterUsers />} />
              <Route path="integrations" element={<MasterIntegrations />} />
              <Route path="api-integrations" element={<MasterApiIntegrations />} />
              <Route path="whatsapp" element={<MasterWhatsApp />} />
              <Route path="whatsapp/companions" element={<MasterCompanions />} />
              <Route path="operations" element={<MasterOperations />} />
              <Route path="security" element={<MasterSecurity />} />
              <Route path="settings" element={<PlatformSettings />} />
            </Route>
          </Route>

          {/* One tenant application, shared by direct URLs and custom domains. */}
            <Route path={TENANT_BASE || "/"} element={<TenantRouteLayout />}>
              {TENANT_BASE && <Route index element={<TenantHome />} />}
              <Route path="plots" element={<AvailablePlots />} />
              <Route path="login" element={<Login />} />
              <Route path="register" element={<CustomerRegister />} />
              <Route element={<Protected roles={STAFF}><Layout /></Protected>}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="settings" element={<Protected roles={ADMINISH}><Settings /></Protected>} />
              </Route>
              <Route path="booking-dashboard" element={<Protected roles={[...STAFF, "sister_company", "sales_dealer"]}><Layout /></Protected>}>
                <Route index element={<BookingDashboard />} />
              </Route>
              <Route path="jobs" element={<Protected roles={[...OPS, "sales_dealer"]}><Layout /></Protected>}>
                <Route index element={<AllJobs />} />
              </Route>

              <Route
                path="receipt/:rid"
                element={
                  <Protected roles={STAFF}>
                    <ReceiptPrint />
                  </Protected>
                }
              />

              <Route path="dealer/receipts/:rid" element={<Protected roles={["sales_dealer"]}><ReceiptPrint dealer /></Protected>} />
              <Route path="admin/bookings/create/challan/:bid" element={<Protected roles={STAFF}><ChallanPrint /></Protected>} />
              <Route path="dealer/bookings/create/challan/:bid" element={<Protected roles={["sales_dealer"]}><ChallanPrint dealer /></Protected>} />
              <Route path="admin/bookings/challan/:bid" element={<Protected roles={STAFF}><ChallanPrint /></Protected>} />
              <Route path="dealer/bookings/challan/:bid" element={<Protected roles={["sales_dealer"]}><ChallanPrint dealer /></Protected>} />

              <Route
                path="portal"
                element={
                  <Protected roles={["customer"]}>
                    <CustomerPortal />
                  </Protected>
                }
              />

              <Route
                path="dealer"
                element={
                  <Protected roles={["sales_dealer"]}>
                    <Layout />
                  </Protected>
                }
              >
                <Route index element={<DealerPortal />} />
                <Route path="plots" element={<DealerPortal view="plots" />} />
                <Route path="bookings" element={<DealerPortal view="bookings" />} />
                <Route path="bookings/create" element={<DealerPortal view="create" />} />
                <Route path="commission" element={<DealerPortal view="commission" />} />
                <Route path="leaderboard" element={<DealerPortal view="leaderboard" />} />
              </Route>

              <Route
                path="admin"
                element={
                  <Protected roles={STAFF}>
                    <Layout />
                  </Protected>
                }
              >
                <Route index element={<Dashboard />} />

                <Route path="projects" element={<Protected roles={STAFF}><Projects /></Protected>} />
                <Route path="blocks" element={<Protected roles={STAFF}><Blocks /></Protected>} />

                <Route
                  path="inventory"
                  element={
                    <Protected roles={AIMGMT}>
                      <PlotInventory />
                    </Protected>
                  }
                />
                <Route
                  path="inventory/dashboard"
                  element={
                    <Protected roles={AIMGMT}>
                      <InventoryDashboard />
                    </Protected>
                  }
                />

                <Route
                  path="bookings"
                  element={
                    <Protected roles={OPS}>
                      <Bookings />
                    </Protected>
                  }
                />
                <Route
                  path="bookings/dashboard"
                  element={
                    <Protected roles={STAFF}>
                      <BookingDashboard />
                    </Protected>
                  }
                />
                <Route
                  path="bookings/create"
                  element={
                    <Protected roles={OPS}>
                      <CreateBooking />
                    </Protected>
                  }
                />
                <Route path="bookings/existing" element={<Protected roles={OPS}><ExistingBooking /></Protected>} />
                <Route path="bookings/confirmed" element={<Protected roles={OPS}><ConfirmedBookings /></Protected>} />
                <Route
                  path="accounting"
                  element={<Protected roles={REPORTS}><Navigate to={tenantUrl("admin/accounting/accounts")} replace /></Protected>}
                />
                {Object.keys(ACCOUNTING_PAGES).map(page => <Route key={`accounting-${page}`} path={`accounting/${page}`} element={<Protected roles={REPORTS}><Accounting page={page} /></Protected>} />)}
                <Route path="finance" element={<Protected roles={[...OPS, "auditor"]}><Navigate to={tenantUrl("admin/finance/vouchers")} replace /></Protected>} />
                {Object.keys(FINANCE_PAGES).map(page => <Route key={`finance-${page}`} path={`finance/${page}`} element={<Protected roles={[...OPS, "auditor"]}><Finance page={page} /></Protected>} />)}
                <Route path="api-integrations" element={<Protected roles={REPORTS}><ApiIntegrations /></Protected>} />
                <Route
                  path="receipts"
                  element={
                    <Protected roles={OPS}>
                      <Receipts />
                    </Protected>
                  }
                />

                <Route
                  path="dealers"
                  element={
                    <Protected roles={ADMINISH}>
                      <Dealers />
                    </Protected>
                  }
                />
                <Route
                  path="dealers/leaderboard"
                  element={
                    <Protected roles={ADMINISH}>
                      <Leaderboard />
                    </Protected>
                  }
                />

                <Route path="conversations" element={<Inbox />} />
                <Route path="conversations/:cid" element={<Inbox />} />
                <Route path="inbox" element={<Inbox />} />
                <Route path="inbox/:cid" element={<Inbox />} />

                <Route path="whatsapp/companions" element={<Protected roles={OPS}><MerchantCompanions /></Protected>} />
                <Route
                  path="whatsapp/messages"
                  element={
                    <Protected roles={LOGS}>
                      <WhatsAppMessages />
                    </Protected>
                  }
                />
                <Route
                  path="whatsapp/failed"
                  element={
                    <Protected roles={AIMGMT}>
                      <FailedMessages />
                    </Protected>
                  }
                />
                <Route
                  path="monitoring"
                  element={
                    <Protected roles={LOGS}>
                      <Monitoring />
                    </Protected>
                  }
                />

                <Route
                  path="contacts"
                  element={
                    <Protected roles={OPS}>
                      <Contacts />
                    </Protected>
                  }
                />
                <Route
                  path="leads"
                  element={
                    <Protected roles={OPS}>
                      <Leads />
                    </Protected>
                  }
                />
                <Route
                  path="appointments"
                  element={
                    <Protected roles={OPS}>
                      <Appointments />
                    </Protected>
                  }
                />
                <Route
                  path="calendar"
                  element={
                    <Protected roles={OPS}>
                      <Calendar />
                    </Protected>
                  }
                />

                <Route
                  path="cases"
                  element={
                    <Protected roles={AIMGMT}>
                      <Cases />
                    </Protected>
                  }
                />
                <Route
                  path="rules"
                  element={
                    <Protected roles={AIMGMT}>
                      <Rules />
                    </Protected>
                  }
                />
                <Route
                  path="automation"
                  element={
                    <Protected roles={AIMGMT}>
                      <AutomationStudio />
                    </Protected>
                  }
                />
                <Route
                  path="knowledge"
                  element={
                    <Protected roles={AIMGMT}>
                      <Knowledge />
                    </Protected>
                  }
                />

                <Route
                  path="logs/webhooks"
                  element={
                    <Protected roles={LOGS}>
                      <Webhooks />
                    </Protected>
                  }
                />
                <Route
                  path="logs/api"
                  element={
                    <Protected roles={LOGS}>
                      <ApiLogs />
                    </Protected>
                  }
                />
                <Route
                  path="logs/audit"
                  element={
                    <Protected roles={LOGS}>
                      <AuditLogs />
                    </Protected>
                  }
                />
                <Route
                  path="logs/trace"
                  element={
                    <Protected roles={LOGS}>
                      <RequestTrace />
                    </Protected>
                  }
                />

                <Route
                  path="reports"
                  element={
                    <Protected roles={REPORTS}>
                      <Reports />
                    </Protected>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <Protected roles={ADMINISH}>
                      <Settings />
                    </Protected>
                  }
                />
                <Route
                  path="roles"
                  element={
                    <Protected roles={ADMINISH}>
                      <Roles />
                    </Protected>
                  }
                />
              </Route>
              <Route path="sister-companies" element={<Protected roles={[...STAFF, "sister_company", "sales_dealer"]}><Layout /></Protected>}>
                <Route index element={<SisterCompanies page="home" />} />
                {Object.keys(SISTER_PAGES).map(page => <Route key={page} path={page} element={<SisterCompanies page={page} />} />)}
              </Route>
            </Route>

          <Route path="*" element={<div>Page not found.</div>} />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>

        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
